import { gql } from "@apollo/client";

// Get tenant's token configuration (branding)
export const GET_PUBLIC_REWARD_TOKEN_CONFIG = gql`
  query PublicRewardTokenConfig {
    publicRewardTokenConfig {
      id
      tokenName
      tokenNamePlural
      tokenIcon
      tokenColor
      tokenValue
      defaultExpiryMonths
      isActive
    }
  }
`;

// Get current user's reward balance
export const GET_MY_REWARD_BALANCE = gql`
  query MyRewardBalance {
    myRewardBalance {
      clientId
      clientName
      activeTokens
      totalValue
      expiringWithin30Days
      owedTokens
      owedValue
    }
  }
`;

// Get current user's reward tokens
export const GET_MY_REWARD_TOKENS = gql`
  query MyRewardTokens {
    myRewardTokens {
      id
      tokenNumber
      clientId
      clientName
      status
      value
      issueReason
      issueDetails
      referredClientName
      issuedAt
      issuedByName
      expiresAt
      redeemedAt
      redeemedFor
      cancelledAt
      cancelledReason
    }
  }
`;

// Get current user's reward transactions
export const GET_MY_REWARD_TRANSACTIONS = gql`
  query MyRewardTransactions {
    myRewardTransactions {
      id
      tokenId
      tokenNumber
      transactionType
      previousStatus
      newStatus
      reason
      details
      performedByName
      performedAt
    }
  }
`;

// Get leaderboard - top token earners (public)
export const GET_REWARD_LEADERBOARD = gql`
  query RewardLeaderboard($limit: Int) {
    rewardLeaderboard(limit: $limit) {
      clientId
      clientName
      totalEarned
      activeTokens
      redeemedTokens
      totalValue
      rank
    }
  }
`;

// Get a single reward token by ID (public - for email links)
export const GET_PUBLIC_REWARD_TOKEN = gql`
  query PublicRewardToken($id: ID!) {
    publicRewardToken(id: $id) {
      id
      tokenNumber
      clientId
      clientName
      status
      value
      issueReason
      issueDetails
      referredClientName
      issuedAt
      issuedByName
      expiresAt
      redeemedAt
      redeemedFor
      cancelledAt
      cancelledReason
    }
  }
`;

// ── IssueReward queries ──

// Get clients for dropdown
export const GET_CLIENTS = gql`
  query GetClients {
    clients {
      id
      fName
      lName
      email
      phoneNumber
    }
  }
`;

// Issue reward mutation
export const ISSUE_REWARD_TOKEN = gql`
  mutation IssueRewardToken($input: IssueRewardTokenInput!) {
    issueRewardToken(input: $input) {
      id
      tokenNumber
      clientName
      value
      issueReason
      issuedAt
      expiresAt
    }
  }
`;

// ── ManageRewards queries ──

// Get all reward tokens (admin)
export const GET_REWARD_TOKENS = gql`
  query GetRewardTokens($filter: RewardTokenFilterInput) {
    rewardTokens(filter: $filter) {
      id
      tokenNumber
      clientId
      clientName
      value
      status
      issueReason
      issueDetails
      issuedAt
      expiresAt
      payableToBank
      redeemedAt
      redeemedFor
    }
  }
`;

// Get stats
export const GET_REWARD_STATS = gql`
  query GetRewardStats {
    rewardTokenStats {
      totalIssued
      totalActive
      totalRedeemed
      totalExpired
      totalCancelled
      issuedThisMonth
      redeemedThisMonth
      expiringThisMonth
    }
  }
`;

// Cancel a reward token
export const CANCEL_REWARD_TOKEN = gql`
  mutation CancelRewardToken($input: CancelRewardTokenInput!) {
    cancelRewardToken(input: $input) {
      id
      status
    }
  }
`;

// Extend a reward token's expiry
export const EXTEND_REWARD_TOKEN = gql`
  mutation ExtendRewardTokenExpiry($input: ExtendRewardTokenExpiryInput!) {
    extendRewardTokenExpiry(input: $input) {
      id
      expiresAt
    }
  }
`;

// ── RewardSettings queries ──

// Get full reward token config (authenticated)
export const GET_REWARD_TOKEN_CONFIG = gql`
  query GetRewardTokenConfig {
    rewardTokenConfig {
      id
      tokenName
      tokenNamePlural
      tokenIcon
      tokenColor
      tokenValue
      defaultExpiryMonths
      emailNotificationEnabled
      smsNotificationEnabled
      isActive
      subscriptionTokenDripEnabled
    }
  }
`;

// Update reward token config
export const UPDATE_REWARD_TOKEN_CONFIG = gql`
  mutation UpdateRewardTokenConfig($input: UpdateRewardTokenConfigInput!) {
    updateRewardTokenConfig(input: $input) {
      id
      tokenName
      tokenNamePlural
      tokenIcon
      tokenColor
      tokenValue
      defaultExpiryMonths
      emailNotificationEnabled
      smsNotificationEnabled
      isActive
      subscriptionTokenDripEnabled
    }
  }
`;

// ==========================================
// EMAIL BRANDING QUERIES (per-module override of tenant.branding for Rewards emails)
// ==========================================

const REWARDS_EMAIL_BRANDING_FIELDS = `
  id
  penName
  emailFooterText
  profilePhotoCid
  signatureImageCid
  bannerImageCid
  profilePhotoUrl
  signatureImageUrl
  bannerImageUrl
  socialLinks { linkedin instagram facebook twitter youtube website }
  ctaButtonText
  ctaButtonUrl
  ctaButtonStyle
  emailStyle
  emailFont
  primaryColor
  secondaryColor
  accentColor
  profilePhotoSize
  socialIconStyle
  hideTagline
  hideSiteName
  previewSampleBody
  createdAt
  updatedAt
`;

export const GET_REWARDS_EMAIL_BRANDING = gql`
  query GetRewardsEmailBranding {
    rewardsEmailBranding {
      ${REWARDS_EMAIL_BRANDING_FIELDS}
    }
  }
`;

export const UPSERT_REWARDS_EMAIL_BRANDING = gql`
  mutation UpsertRewardsEmailBranding($input: RewardsEmailBrandingInput!) {
    upsertRewardsEmailBranding(input: $input) {
      ${REWARDS_EMAIL_BRANDING_FIELDS}
    }
  }
`;

export const PREVIEW_REWARDS_EMAIL_BRANDING = gql`
  query PreviewRewardsEmailBranding($input: RewardsEmailBrandingInput!, $sampleBody: String) {
    previewRewardsEmailBranding(input: $input, sampleBody: $sampleBody)
  }
`;

export const SEND_TEST_REWARDS_EMAIL = gql`
  mutation SendTestRewardsEmail($html: String!, $subject: String) {
    sendTestRewardsEmail(html: $html, subject: $subject)
  }
`;

// ==================== IOUs — what this site owes ME (banking module, 2026-09-22) ====================
// The lender presses "Redeem for AUD" on an IOU token; banking puts "Pay back <me>" on the admin's month.
export const GET_MY_BANKING_IOUS = gql`
  query RewardsMyBankingIous {
    myBankingIous {
      balanceMinor
      tokens {
        rewardTokenId
        tokenNumber
        amountMinor
        stillOwedMinor
        lentAt
        status
        payoutRequested
        payoutJobId
      }
    }
  }
`;

export const REQUEST_BANKING_IOU_PAYOUT = gql`
  mutation RewardsRequestBankingIouPayout($rewardTokenId: ID!) {
    requestBankingIouPayout(rewardTokenId: $rewardTokenId) {
      alreadyRequested
      job {
        id
        month
        title
        expectedMinor
      }
    }
  }
`;

// ==================== Payouts to my bank (2026-10-10) ====================
// Payable reward tokens are money owed to the holder. "Request payout" puts "Pay <me>" on the
// admin's banking month; nothing moves money until the admin confirms it was paid.
const PAYOUT_REQUEST_FIELDS = `
  id
  clientId
  clientName
  tokenNumbers
  amountMinor
  status
  maskedAccount
  requestedAt
  paidAt
  paidByName
  paymentRef
  cancelledAt
`;

export const GET_MY_REWARD_PAYOUTS = gql`
  query RewardsMyRewardPayouts {
    myRewardPayouts {
      availableMinor
      availableTokenCount
      bankDetails {
        onFile
        accountName
        bsbMasked
        accountLast4
      }
      requests {
        ${PAYOUT_REQUEST_FIELDS}
      }
    }
  }
`;

export const SET_MY_REWARD_PAYOUT_BANK_DETAILS = gql`
  mutation RewardsSetMyRewardPayoutBankDetails($input: SetRewardPayoutBankDetailsInput!) {
    setMyRewardPayoutBankDetails(input: $input) {
      onFile
      accountName
      bsbMasked
      accountLast4
    }
  }
`;

export const REQUEST_REWARD_PAYOUT = gql`
  mutation RewardsRequestRewardPayout {
    requestRewardPayout {
      alreadyRequested
      request {
        ${PAYOUT_REQUEST_FIELDS}
      }
    }
  }
`;

export const CANCEL_MY_REWARD_PAYOUT = gql`
  mutation RewardsCancelMyRewardPayout($requestId: ID!) {
    cancelMyRewardPayout(requestId: $requestId) {
      ${PAYOUT_REQUEST_FIELDS}
    }
  }
`;

export const GET_REWARD_PAYOUT_REQUESTS = gql`
  query RewardsRewardPayoutRequests($status: RewardPayoutStatus) {
    rewardPayoutRequests(status: $status) {
      ${PAYOUT_REQUEST_FIELDS}
    }
  }
`;

export const REVEAL_REWARD_PAYOUT_BANK_DETAILS = gql`
  query RewardsRevealRewardPayoutBankDetails($requestId: ID!) {
    revealRewardPayoutBankDetails(requestId: $requestId) {
      accountName
      bsb
      accountNumber
    }
  }
`;

export const MARK_REWARD_PAYOUT_PAID = gql`
  mutation RewardsMarkRewardPayoutPaid($input: MarkRewardPayoutPaidInput!) {
    markRewardPayoutPaid(input: $input) {
      shortfallTokenNumbers
      request {
        ${PAYOUT_REQUEST_FIELDS}
      }
    }
  }
`;

export const CANCEL_REWARD_PAYOUT = gql`
  mutation RewardsCancelRewardPayout($requestId: ID!) {
    cancelRewardPayout(requestId: $requestId) {
      ${PAYOUT_REQUEST_FIELDS}
    }
  }
`;

export const MAKE_REWARD_TOKEN_PAYABLE = gql`
  mutation RewardsMakeRewardTokenPayable($tokenId: ID!) {
    makeRewardTokenPayable(tokenId: $tokenId) {
      id
      expiresAt
      payableToBank
    }
  }
`;
